import { downloadData } from '@va/http-client';
import domtoimage, { Options } from 'dom-to-image';
import { jsPDF } from 'jspdf';
import { MutableRefObject, useCallback, useMemo } from 'react';
import { useAddNotification } from './useAddNotification';
import { useVerboseFilename } from './useVerboseFilename';

export const useDownloadScreenshot = (
  elRef: MutableRefObject<HTMLDivElement | null>,
  type: string,
  options?: Options,
) => {
  const { showErrorNotification } = useAddNotification();

  const { createVerboseFilename } = useVerboseFilename();

  // dom-to-image copies the element's margins to the clone but sizes the image without them,
  // so e.g. a `space-y-*` top margin shifts the content down and cuts off the bottom
  const screenshotOptions = useMemo<Options>(
    () => ({ ...options, style: { margin: '0', ...options?.style } }),
    [options],
  );

  const getFilename = useCallback(
    (extension?: string) => {
      return createVerboseFilename({ extension, fileName: type });
    },
    [createVerboseFilename, type],
  );

  const downloadPNGfunc = useCallback(async () => {
    const el = elRef.current;
    if (!el) return;

    el.classList.add('on-screenshot');

    try {
      const imgData = await domtoimage.toPng(el, screenshotOptions);
      downloadData(imgData, getFilename('png'));
    } catch (error) {
      console.error(error);
      showErrorNotification();
    } finally {
      el.classList.remove('on-screenshot');
    }
  }, [getFilename, elRef, screenshotOptions, showErrorNotification]);

  const downloadPDFfunc = useCallback(async () => {
    const el = elRef.current;
    if (!el) return;
    el.classList.add('on-screenshot');
    try {
      const imgData = await domtoimage.toPng(el, screenshotOptions);
      const doc = new jsPDF('landscape', 'mm');
      const w = doc.internal.pageSize.getWidth();
      doc.addImage(imgData, 'PNG', 0, 0, w, 0);
      doc.save(getFilename('pdf'));
    } catch (error) {
      console.error(error);
      showErrorNotification();
    } finally {
      el.classList.remove('on-screenshot');
    }
  }, [getFilename, elRef, screenshotOptions, showErrorNotification]);

  return { downloadPDFfunc, downloadPNGfunc };
};
